// components/Dashboard/Resource.jsx
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react"
import {
  Card,
  Col,
  Row,
  Table,
  Tag,
  Typography,
  Space,
  Tooltip,
  Progress,
  Select,
  Alert,
  Button,
  Modal,
  Spin,
} from "antd"
import { UserOutlined, ClockCircleOutlined, PieChartOutlined } from "@ant-design/icons"

import useDashboardRepo from "services/repo/useDashboardRepo"
import LocalStorageUtils from "utils/LocalStorage"

const { Title, Text } = Typography

// ======================= Helpers =======================
const toNum = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// IFG mapping
const IFG_KEY_MAP = {
  Underload: "under",
  Normal: "normal",
  Stretched: "stretched",
  Overload: "over",
}

const IFG_LABEL = {
  under: "Underload",
  normal: "Normal",
  stretched: "Stretched",
  over: "Overload",
}

const IFG_COLOR = {
  under: "#1677ff",
  normal: "#52c41a",
  stretched: "#faad14",
  over: "#ff4d4f",
}

// ======================= Ring =======================
function LevelRing({ utilizationCategories, centerValue, size = 170, thickness = 30 }) {
  const segments = [
    { color: utilizationCategories.over.color, value: utilizationCategories.over.percent },
    { color: utilizationCategories.stretched.color, value: utilizationCategories.stretched.percent },
    { color: utilizationCategories.under.color, value: utilizationCategories.under.percent },
    { color: utilizationCategories.normal.color, value: utilizationCategories.normal.percent },
  ]

  let acc = 0
  const stops = segments
    .map((s) => {
      const start = acc
      acc += s.value
      return `${s.color} ${start}% ${acc}%`
    })
    .join(", ")

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: "50%",
        background: `conic-gradient(${stops})`,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: thickness,
          borderRadius: "50%",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <div style={{ lineHeight: 1.1 }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{(centerValue ?? 0).toLocaleString()}</div>
          <div style={{ fontSize: 12 }}>Group</div>
        </div>
      </div>
    </div>
  )
}

// ======================= Pivot: Div matrix =======================
function pivotDivMatrix(list = []) {
  const byDiv = new Map()

  list.forEach((x) => {
    const divId = x?.divId || "-"
    const divName = x?.divName || "-"
    const key = IFG_KEY_MAP[x?.ifgName]
    const cnt = toNum(x?.ifgTotal)

    if (!byDiv.has(divId)) {
      byDiv.set(divId, {
        key: divId,
        divId,
        divName,
        under: 0,
        normal: 0,
        stretched: 0,
        over: 0,
        total: 0,
      })
    }

    const row = byDiv.get(divId)
    if (key) row[key] = cnt
  })

  const rows = Array.from(byDiv.values()).map((r) => ({
    ...r,
    total: toNum(r.under) + toNum(r.normal) + toNum(r.stretched) + toNum(r.over),
  }))

  rows.sort((a, b) => toNum(b.total) - toNum(a.total))
  return rows
}

// ======================= Pivot: Dept matrix =======================
function pivotDeptMatrix(list = []) {
  const byDept = new Map()

  list.forEach((x) => {
    const deptId = x?.deptId || "-"
    const deptName = x?.deptName || "-"
    const key = IFG_KEY_MAP[x?.ifgName]
    const cnt = toNum(x?.ifgTotal)

    if (!byDept.has(deptId)) {
      byDept.set(deptId, {
        key: deptId,
        deptId,
        deptName,
        under: 0,
        normal: 0,
        stretched: 0,
        over: 0,
        total: 0,
      })
    }

    const row = byDept.get(deptId)
    if (key) row[key] = cnt
  })

  const rows = Array.from(byDept.values()).map((r) => ({
    ...r,
    total: toNum(r.under) + toNum(r.normal) + toNum(r.stretched) + toNum(r.over),
  }))

  rows.sort((a, b) => toNum(b.total) - toNum(a.total))
  return rows
}

// ======================= Main =======================
export default function Resource() {
  const { getCau, getIfgSummary, getIfg, getIfgDiv, getIfgDept } = useDashboardRepo(false)

  const [loginOrgId, setLoginOrgId] = useState(null)
  const [loginWcId, setLoginWcId] = useState(null)
  const [orgName, setOrgName] = useState("")
  const [infoText, setInfoText] = useState("")

  const [loadingTop, setLoadingTop] = useState(false)
  const [loadingDiv, setLoadingDiv] = useState(false)

  // ===== Summary =====
  const [summary, setSummary] = useState({
    capacityHours: 0,
    actualHours: 0,
    utilizationPct: 0,
    totalPositions: 0,
    utilizationCategories: {
      under: { key: "under", label: "Underload", percent: 0, count: 0, color: IFG_COLOR.under },
      normal: { key: "normal", label: "Normal", percent: 0, count: 0, color: IFG_COLOR.normal },
      stretched: { key: "stretched", label: "Stretched", percent: 0, count: 0, color: IFG_COLOR.stretched },
      over: { key: "over", label: "Overload", percent: 0, count: 0, color: IFG_COLOR.over },
    },
  })

  // ===== ids for drilldown =====
  const [tdifgId, setTdifgId] = useState(null)
  const [ifgIdByKey, setIfgIdByKey] = useState({ under: null, normal: null, stretched: null, over: null })

  // ===== Rekap table (Div) =====
  const [divRows, setDivRows] = useState([])

  // ===== Active bucket =====
  const [activeBucket, setActiveBucket] = useState("under")

  // ===== Modal Dept Matrix =====
  const [deptModalOpen, setDeptModalOpen] = useState(false)
  const [deptModalLoading, setDeptModalLoading] = useState(false)
  const [deptModalTitle, setDeptModalTitle] = useState("")
  const [deptRows, setDeptRows] = useState([])
  const [selectedDiv, setSelectedDiv] = useState(null)

  // ===== Modal Pos List =====
  const [posModalOpen, setPosModalOpen] = useState(false)
  const [posModalLoading, setPosModalLoading] = useState(false)
  const [posModalTitle, setPosModalTitle] = useState("")
  const [posRows, setPosRows] = useState([])
  const [selectedDept, setSelectedDept] = useState(null)

  const didInitRef = useRef(false)

  // ======================= Fetch All (CAU + IFG Summary + IFG Div Table) =======================
  const fetchAll = useCallback(async () => {
    if (!loginOrgId || !loginWcId) return

    setInfoText("")
    setLoadingTop(true)

    const payload = { orgId: loginOrgId, wcId: loginWcId }

    try {
      // ---- CAU ----
      const cauRes = await getCau(payload)
      if (cauRes?.status === 200 && cauRes?.detail === "OK") {
        const d = cauRes?.data ?? {}
        setSummary((prev) => ({
          ...prev,
          capacityHours: toNum(d?.tdcauCap),
          actualHours: toNum(d?.tdcauAct),
          utilizationPct: toNum(d?.tdcauUtil),
          totalPositions: toNum(d?.tdcauTotalApprovedPosition),
        }))
      } else if (cauRes?.status === 404) {
        setInfoText(cauRes?.detail || "Data CAU tidak ditemukan.")
        setSummary((prev) => ({ ...prev, capacityHours: 0, actualHours: 0, utilizationPct: 0, totalPositions: 0 }))
      }

      // ---- IFG SUMMARY (ambil percent/count + ifgId mapping) ----
      const ifgRes = await getIfgSummary(payload)
      if (ifgRes?.status === 200 && ifgRes?.detail === "OK") {
        const list = ifgRes?.data?.detailList ?? []

        const nextIfgIdMap = { under: null, normal: null, stretched: null, over: null }
        list.forEach((x) => {
          const key = IFG_KEY_MAP[x?.ifgName]
          if (key) nextIfgIdMap[key] = x?.ifgId || null
        })
        setIfgIdByKey(nextIfgIdMap)

        setSummary((prev) => {
          const nextCats = { ...prev.utilizationCategories }
          Object.keys(nextCats).forEach((k) => (nextCats[k] = { ...nextCats[k], percent: 0, count: 0 }))

          list.forEach((x) => {
            const key = IFG_KEY_MAP[x?.ifgName]
            if (!key) return
            nextCats[key] = {
              ...nextCats[key],
              label: x?.ifgName || nextCats[key].label,
              percent: toNum(x?.ifgPercentage),
              count: toNum(x?.ifgTotal),
            }
          })

          return { ...prev, utilizationCategories: nextCats }
        })
      }

      // ---- IFG DIV MATRIX (rekap table) ----
      setLoadingDiv(true)
      const ifgDivRes = await getIfg(payload)
      setLoadingDiv(false)

      if (ifgDivRes?.status === 200 && ifgDivRes?.detail === "OK") {
        setTdifgId(ifgDivRes?.data?.tdifgId || null)
        const list = ifgDivRes?.data?.dashboardIfgDivList ?? []
        setDivRows(pivotDivMatrix(list))
      } else if (ifgDivRes?.status === 404) {
        setDivRows([])
        setInfoText(ifgDivRes?.detail || "Data IFG (rekap divisi) tidak ditemukan.")
      }
    } finally {
      setLoadingDiv(false)
      setLoadingTop(false)
    }
  }, [loginOrgId, loginWcId, getCau, getIfgSummary, getIfg])

  // ======================= Init from LocalStorage (1x) =======================
  // 1) Init: baca localStorage
  useEffect(() => {
    const userInfo = LocalStorageUtils.userInfo.get()
    const orgId = userInfo?.orgId || null
    const wcId = userInfo?.wcId || null
    const orgNameLS = userInfo?.OrgName || userInfo?.orgName || ""

    setLoginOrgId(orgId)
    setLoginWcId(wcId)
    setOrgName(orgNameLS)

    if (!orgId) setInfoText("orgId tidak ditemukan dari login.")
    if (!wcId) setInfoText("wcId tidak ditemukan dari login.")
  }, [])

  // 2) Auto-load 1x: setelah orgId+wcId ready
  const didAutoFetchRef = useRef(false)
  useEffect(() => {
    if (!loginOrgId || !loginWcId) return
    if (didAutoFetchRef.current) return
    didAutoFetchRef.current = true
    fetchAll()
  }, [loginOrgId, loginWcId, fetchAll])

  // ======================= Drilldown: open Dept modal =======================
  const openDeptModal = useCallback(
    async (divRow, bucketKey) => {
      if (!loginOrgId || !tdifgId) return
      if (!divRow?.divId) return

      setActiveBucket(bucketKey)
      setSelectedDiv({ divId: divRow.divId, divName: divRow.divName })
      setDeptModalTitle(`${divRow.divName} - ${IFG_LABEL[bucketKey]}`)
      setDeptModalOpen(true)
      setDeptModalLoading(true)
      setDeptRows([])

      const payload = { tdifgId, orgId: loginOrgId, divId: divRow.divId }
      const res = await getIfgDiv(payload)

      setDeptModalLoading(false)
      if (res?.status === 200 && res?.detail === "OK") {
        const list = res?.data?.dashboardIfgDeptList ?? []
        setDeptRows(pivotDeptMatrix(list))
      } else {
        setDeptRows([])
        setInfoText(res?.detail || "Data departemen tidak ditemukan.")
      }
    },
    [loginOrgId, tdifgId, getIfgDiv]
  )

  // ======================= Drilldown: open Pos modal =======================
  const openPosModal = useCallback(
    async (deptRow, bucketKey) => {
      if (!loginOrgId || !tdifgId) return
      if (!selectedDiv?.divId) return
      if (!deptRow?.deptId) return

      const ifgId = ifgIdByKey?.[bucketKey]
      if (!ifgId) {
        setInfoText("ifgId tidak ditemukan untuk bucket ini. Pastikan /dashboard/ifg/summary sukses.")
        return
      }

      setActiveBucket(bucketKey)
      setSelectedDept({ deptId: deptRow.deptId, deptName: deptRow.deptName })
      setPosModalTitle(`${deptRow.deptName} - ${IFG_LABEL[bucketKey]}`)
      setPosModalOpen(true)
      setPosModalLoading(true)
      setPosRows([])

      const payload = {
        orgId: loginOrgId,
        tdifgId,
        divId: selectedDiv.divId,
        deptId: deptRow.deptId,
        ifgId,
      }

      const res = await getIfgDept(payload)

      setPosModalLoading(false)
      if (res?.status === 200 && res?.detail === "OK") {
        setPosRows(Array.isArray(res?.data?.dashboardIfgPosList) ? res.data.dashboardIfgPosList : [])
      } else {
        setPosRows([])
        setInfoText(res?.detail || "Data posisi tidak ditemukan.")
      }
    },
    [loginOrgId, tdifgId, selectedDiv, ifgIdByKey, getIfgDept]
  )

  // ======================= UI computed =======================
  const utilizationCards = useMemo(
    () => [
      summary.utilizationCategories.under,
      summary.utilizationCategories.normal,
      summary.utilizationCategories.over,
    ],
    [summary.utilizationCategories]
  )

  const orgOptions = useMemo(() => {
    if (!loginOrgId) return []
    return [{ label: orgName || "Organisasi", value: loginOrgId }]
  }, [loginOrgId, orgName])

  // ======================= Columns: Rekap DIV (Design) =======================
  const divColumns = useMemo(() => {
    const countCell = (bucketKey) => (value, row) => (
      <a onClick={() => openDeptModal(row, bucketKey)}>{toNum(value)}</a>
    )

    return [
      { title: "Divisi", dataIndex: "divName", key: "divName" },
      { title: "Underload", dataIndex: "under", key: "under", align: "center", render: countCell("under") },
      { title: "Normal", dataIndex: "normal", key: "normal", align: "center", render: countCell("normal") },
      { title: "Stretched", dataIndex: "stretched", key: "stretched", align: "center", render: countCell("stretched") },
      { title: "Overload", dataIndex: "over", key: "over", align: "center", render: countCell("over") },
      { title: "Total", dataIndex: "total", key: "total", align: "center" },
    ]
  }, [openDeptModal])

  // ======================= Columns: Dept modal =======================
  const deptColumns = useMemo(() => {
    const countCell = (bucketKey) => (value, row) => (
      <a onClick={() => openPosModal(row, bucketKey)}>{toNum(value)}</a>
    )

    return [
      { title: "Departemen", dataIndex: "deptName", key: "deptName" },
      { title: "Underload", dataIndex: "under", key: "under", align: "center", render: countCell("under") },
      { title: "Normal", dataIndex: "normal", key: "normal", align: "center", render: countCell("normal") },
      { title: "Stretched", dataIndex: "stretched", key: "stretched", align: "center", render: countCell("stretched") },
      { title: "Overload", dataIndex: "over", key: "over", align: "center", render: countCell("over") },
      { title: "Total", dataIndex: "total", key: "total", align: "center" },
    ]
  }, [openPosModal])

  // ======================= Columns: Pos list (flex until schema fixed) =======================
  const posColumns = useMemo(() => {
    return [
      { title: "No WBS Form", dataIndex: "wbsFormNo", key: "wbsFormNo", render: (v, r) => v || r?.formNo || "-" },
      { title: "Nama", dataIndex: "employeeName", key: "employeeName", render: (v, r) => v || r?.name || r?.fullName || "-" },
      { title: "Position", dataIndex: "positionName", key: "positionName", render: (v, r) => v || r?.position || "-" },
      { title: "Dept", dataIndex: "deptName", key: "deptName", render: (v, r) => v || "-" },
      { title: "FTE", dataIndex: "fte", key: "fte", align: "center", render: (v, r) => v ?? r?.fteValue ?? "-" },
    ]
  }, [])

  return (
    <div style={{ padding: 24 }}>
      {infoText ? (
        <div style={{ marginBottom: 12 }}>
          <Alert type="warning" showIcon message={infoText} />
        </div>
      ) : null}

      {/* Top bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space size={8} wrap>
              <Text type="secondary">Organisasi</Text>
              <Select
                value={loginOrgId}
                style={{ minWidth: 360 }}
                options={orgOptions}
                disabled
                placeholder="Organization (from login)"
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
              {/* <Tag color="blue">wcId: {loginWcId ? "OK" : "N/A"}</Tag> */}
              

            </Space>
          </Col>

          <Col>
            <Space>
              <Tag color="blue">{activeBucket.toUpperCase()}</Tag>
              <Button type="primary" onClick={fetchAll} loading={loadingTop}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>

        {/* <div style={{ marginTop: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            * Klik Refresh untuk load data (anti looping).
          </Text>
        </div> */}
      </Card>

      {/* Summary area */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} md={24}>
          <Card style={{ height: "100%" }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              Selamat Datang
            </Title>
            <Text type="secondary">
              Laporan menginformasikan distribusi dan perbandingan beban kerja karyawan dalam suatu kelompok kerja berdasarkan jumlah jam kerja aktual dan kapasitas hari kerja.
            </Text>

            <Row gutter={[12, 12]} style={{ marginTop: 24 }}>
              <Col xs={24} sm={8}>
                <Card size="small" bordered={false} style={{ background: "#F6FFED" }}>
                  <Space align="baseline">
                    <ClockCircleOutlined />
                    <Text strong>Kapasitas (Jam)</Text>
                  </Space>
                  <Title level={3} style={{ margin: 0 }}>
                    {summary.capacityHours} Jam
                  </Title>
                </Card>
              </Col>

              <Col xs={24} sm={8}>
                <Card size="small" bordered={false} style={{ background: "#E6F7FF" }}>
                  <Space align="baseline">
                    <UserOutlined />
                    <Text strong>Aktual (Jam)</Text>
                  </Space>
                  <Title level={3} style={{ margin: 0 }}>
                    {summary.actualHours} Jam
                  </Title>
                </Card>
              </Col>

              <Col xs={24} sm={8}>
                <Card size="small" bordered={false} style={{ background: "#FFF7E6" }}>
                  <Space align="baseline">
                    <PieChartOutlined />
                    <Text strong>Utilisasi</Text>
                  </Space>
                  <Title level={3} style={{ margin: 0 }}>
                    {summary.utilizationPct}%
                  </Title>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 20 }} align="stretch">
              <Col xs={24} md={14}>
                <Row gutter={[16, 16]}>
                  {utilizationCards.map((c) => (
                    <Col xs={24} sm={12} md={8} key={c.key}>
                      <Card
                        size="small"
                        onClick={() => setActiveBucket(c.key)}
                        style={{
                          borderColor: activeBucket === c.key ? c.color : undefined,
                          cursor: "pointer",
                        }}
                        bodyStyle={{ paddingBottom: 10 }}
                      >
                        <Space style={{ width: "100%", justifyContent: "space-between" }}>
                          <Text strong style={{ color: c.color }}>
                            {c.label}
                          </Text>
                          <Tooltip title={`${c.percent}% dari total group`}>
                            <Progress type="circle" percent={c.percent} size={40} strokeColor={c.color} trailColor="#f0f0f0" />
                          </Tooltip>
                        </Space>
                        <Text type="secondary">{(c.count ?? 0).toLocaleString()} Group</Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>

              <Col xs={24} md={10}>
                <div style={{ height: "100%", display: "flex", alignItems: "center", gap: 16 }}>
                  <LevelRing utilizationCategories={summary.utilizationCategories} centerValue={summary.totalPositions} />
                  <div>
                    <Title level={5} style={{ marginBottom: 8 }}>
                      Level
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3, display: "block" }}>
                      * Warna pada grafik menunjukkan kategori utilisasi beban kerja di setiap group.
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

      </Row>

      {/* Rekapitulasi DIV table (Design) */}
      <Card
        title={
          <Space>
            <span>Rekapitulasi</span>
            <Tag color="default">{divRows.length}</Tag>
          </Space>
        }
        style={{ marginTop: 20 }}
        extra={<Text type="secondary">* Klik angka untuk detail</Text>}
      >
        <Spin spinning={loadingDiv}>
          <Table
            columns={divColumns}
            dataSource={divRows}
            rowKey="key"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        </Spin>
      </Card>

      {/* Modal Dept matrix */}
      <Modal
        open={deptModalOpen}
        title={deptModalTitle}
        onCancel={() => {
          setDeptModalOpen(false)
          setDeptRows([])
          setSelectedDiv(null)
        }}
        footer={null}
        width={1000}
      >
        <Spin spinning={deptModalLoading}>
          <Table
            columns={deptColumns}
            dataSource={deptRows}
            rowKey="key"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{ emptyText: "Tidak ada data departemen." }}
          />
        </Spin>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            * Klik angka pada departemen untuk membuka detail posisi.
          </Text>
        </div>
      </Modal>

      {/* Modal Pos list */}
      <Modal
        open={posModalOpen}
        title={posModalTitle}
        onCancel={() => {
          setPosModalOpen(false)
          setPosRows([])
          setSelectedDept(null)
        }}
        footer={null}
        width={1100}
      >
        <Spin spinning={posModalLoading}>
          <Table
            columns={posColumns}
            dataSource={posRows}
            rowKey={(r, idx) => r?.id || r?.posId || `${idx}`}
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{ emptyText: "Tidak ada data posisi." }}
          />
        </Spin>
      </Modal>
    </div>
  )
}
